// PiezaPlanoMaterial — chips "Plano" y "Material bruto" de una pieza del catálogo,
// con acción inmediata si falta alguno (15-sep-2026, pedido Maxi).
//
// Se usa al registrar una OC de cliente y al cotizar: si la pieza no tiene plano o no
// tiene definido el material en bruto, la persona lo completa ahí mismo, sin ir al
// catálogo. Plano → POST /control-dimensional/:id/plano-maestro (capa storage, la misma
// del control dimensional). Material → PATCH /piece {materialRef, consumoPorUnidad}.
// Sin nada de eso el trabajo entra igual: son avisos con acción, no bloqueos.
import React, { useEffect, useRef, useState } from "react";
import backend from "../backend";

let cacheMateriales = null;
const cargarMateriales = async () => {
  if (cacheMateriales) return cacheMateriales;
  try { cacheMateriales = (await backend.get("rawMaterial")).data || []; }
  catch { cacheMateriales = []; }
  return cacheMateriales;
};

const chip = (ok, extra = {}) => ({
  display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11, padding: "3px 9px", borderRadius: 5,
  background: ok ? "#0d2a15" : "#2a1d08", color: ok ? "#2ecc71" : "#f39c12",
  border: `1px solid ${ok ? "#0d4020" : "#4a3410"}`, whiteSpace: "nowrap", ...extra,
});
const btn = { background: "transparent", color: "inherit", border: "1px solid currentColor", borderRadius: 4, padding: "1px 7px", fontSize: 10, cursor: "pointer" };

const urlPlano = p => p?.planoMaestro?.archivo?.url || p?.blueprint?.url || null;

const PiezaPlanoMaterial = ({ pieza, onChange, compacto = false }) => {
  const fileRef = useRef();
  const [subiendo, setSubiendo] = useState(false);
  const [editMat, setEditMat] = useState(false);
  const [materiales, setMateriales] = useState([]);
  const [matSel, setMatSel] = useState("");
  const [consumo, setConsumo] = useState(String(pieza?.consumoPorUnidad ?? 1));
  const [error, setError] = useState("");

  useEffect(() => { if (editMat) cargarMateriales().then(setMateriales); }, [editMat]);
  if (!pieza?._id) return null;

  const plano = urlPlano(pieza);
  const mat = pieza.materialRef;
  const matNombre = mat && typeof mat === "object" ? mat.name : null;
  const tieneMat = !!(mat && (typeof mat === "string" || mat._id));

  const subirPlano = async e => {
    const f = e.target.files?.[0];
    if (!f) return;
    setError(""); setSubiendo(true);
    try {
      const fd = new FormData();
      fd.append("plano", f);
      const r = await backend.post(`control-dimensional/${pieza._id}/plano-maestro`, fd);
      onChange?.({ planoMaestro: r.data });
    } catch (err) {
      setError(err?.response?.data?.error || err?.response?.data || err.message || "No se pudo subir el plano");
    } finally { setSubiendo(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const guardarMaterial = async () => {
    if (!matSel) { setError("Elige un material"); return; }
    setError("");
    try {
      await backend.patch("piece", { _id: pieza._id, materialRef: matSel, consumoPorUnidad: Number(consumo) || 1 });
      const m = materiales.find(x => x._id === matSel);
      onChange?.({ materialRef: m || matSel, consumoPorUnidad: Number(consumo) || 1 });
      setEditMat(false);
    } catch (err) {
      setError(err?.response?.data?.error || err?.response?.data || err.message || "No se pudo guardar el material");
    }
  };

  return (
    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginTop: compacto ? 2 : 4 }}>
      {plano ? (
        <a href={plano} target="_blank" rel="noreferrer" style={{ ...chip(true), textDecoration: "none" }} title="Abrir plano">📄 Plano ✓</a>
      ) : (
        <span style={chip(false)}>
          📄 Sin plano
          <button type="button" style={btn} disabled={subiendo} onClick={() => fileRef.current?.click()}>
            {subiendo ? "Subiendo…" : "Subir PDF"}
          </button>
          <input ref={fileRef} type="file" accept=".pdf,.png,.jpg,.jpeg" hidden onChange={subirPlano} />
        </span>
      )}

      {tieneMat && !editMat ? (
        <span style={chip(true)} title="Material en bruto que se compra para fabricar esta pieza">
          🧱 {matNombre || "Material asignado"} · {pieza.consumoPorUnidad ?? 1}/u
          <button type="button" style={btn} onClick={() => { setMatSel(typeof mat === "object" ? mat._id : mat); setEditMat(true); }}>cambiar</button>
        </span>
      ) : !editMat ? (
        <span style={chip(false)}>
          🧱 Sin material en bruto
          <button type="button" style={btn} onClick={() => setEditMat(true)}>Asignar</button>
        </span>
      ) : (
        <span style={{ ...chip(false), gap: 6 }}>
          <select value={matSel} onChange={e => setMatSel(e.target.value)}
            style={{ background: "#161616", color: "#ddd", border: "1px solid #444", borderRadius: 4, fontSize: 11, maxWidth: 220 }}>
            <option value="">Material…</option>
            {materiales.map(m => <option key={m._id} value={m._id}>{m.name}{m.unit ? ` (${m.unit})` : ""}</option>)}
          </select>
          <input type="number" min="0.01" step="0.01" value={consumo} onChange={e => setConsumo(e.target.value)}
            title="Consumo por unidad de pieza" style={{ width: 62, background: "#161616", color: "#ddd", border: "1px solid #444", borderRadius: 4, fontSize: 11 }} />
          <span style={{ fontSize: 10, color: "#999" }}>por unidad</span>
          <button type="button" style={btn} onClick={guardarMaterial}>Guardar</button>
          <button type="button" style={{ ...btn, opacity: 0.7 }} onClick={() => { setEditMat(false); setError(""); }}>Cancelar</button>
        </span>
      )}
      {error && <span style={{ fontSize: 10, color: "#e74c3c" }}>{String(error).slice(0, 120)}</span>}
    </div>
  );
};

export default PiezaPlanoMaterial;
